import { getRouteIdFromPathname, RouteId, routes } from '@trello/routes';

import {
  WorkspaceState,
  WorkspaceStateStatus,
  WorkspaceStateError,
} from '../workspaceState';

import { getWorkspaceForBoard } from './getWorkspaceForBoard';
import { getWorkspaceForCard } from './getWorkspaceForCard';
import { getWorkspaceForOrganization } from './getWorkspaceForOrganization';
import { getGlobalWorkspace } from './getGlobalWorkspace';

interface WorkspaceGetter {
  (...args: string[]): Promise<WorkspaceState>;
}

const noMatchingRouteError = (path: string) =>
  Promise.resolve({
    status: WorkspaceStateStatus.ERROR,
    error: WorkspaceStateError.NO_MATCHING_ROUTE,
    isGlobal: false,
    idWorkspace: null,
  });

export const workspaceGetterMap: Record<RouteId, WorkspaceGetter> = {
  [RouteId.ACCOUNT]: getWorkspaceForOrganization,
  [RouteId.BILLING]: getWorkspaceForOrganization,
  [RouteId.BILLING_CARD]: getWorkspaceForOrganization,
  [RouteId.BILLING_CARD_PRODUCT]: getWorkspaceForOrganization,
  [RouteId.BOARD]: getWorkspaceForBoard,
  [RouteId.BOARD_DASHBOARD]: getWorkspaceForBoard,

  [RouteId.BOARD_OLD]: (path: string) => {
    const parts = path.split('/');
    const idBoard = parts.length > 1 ? parts[1] : parts[0];
    return getWorkspaceForBoard(idBoard);
  },

  [RouteId.BOARD_REFERRAL]: getWorkspaceForBoard,
  [RouteId.CARD]: getWorkspaceForCard,

  [RouteId.CARD_OLD]: (...params: string[]) => {
    const idBoard = params.length > 2 ? params[1] : params[0];
    return getWorkspaceForBoard(idBoard);
  },

  [RouteId.CARD_AND_BOARD_OLD]: (...params: string[]) => {
    const idBoard = params.length > 2 ? params[1] : params[0];
    return getWorkspaceForBoard(idBoard);
  },

  [RouteId.MEMBER_CARDS_FOR_ORG]: getWorkspaceForOrganization,
  [RouteId.MEMBER_HOME_BOARDS]: getWorkspaceForOrganization,
  [RouteId.ORGANIZATION_BY_ID]: getWorkspaceForOrganization,
  [RouteId.ORGANIZATION_EXPORT]: getWorkspaceForOrganization,
  [RouteId.ORGANIZATION_FREE_TRIAL]: getWorkspaceForOrganization,
  [RouteId.ORGANIZATION_GUESTS]: getWorkspaceForOrganization,
  [RouteId.ORGANIZATION_MEMBER_CARDS]: getWorkspaceForOrganization,
  [RouteId.ORGANIZATION_MEMBERS]: getWorkspaceForOrganization,
  [RouteId.ORGANIZATION_POWER_UPS]: getWorkspaceForOrganization,
  [RouteId.ORGANIZATION_TABLES]: getWorkspaceForOrganization,
  [RouteId.PROFILE]: getWorkspaceForOrganization,
  [RouteId.TEAM_GETTING_STARTED]: getWorkspaceForOrganization,
  [RouteId.TEAM_HIGHLIGHTS]: getWorkspaceForOrganization,
  [RouteId.TEAM_REPORTS]: getWorkspaceForOrganization,
  [RouteId.USER_OR_ORG]: getWorkspaceForOrganization,

  [RouteId.BLANK]: getGlobalWorkspace,
  [RouteId.CREATE_FIRST_BOARD]: getGlobalWorkspace,
  [RouteId.CREATE_FIRST_TEAM]: getGlobalWorkspace,
  [RouteId.DOUBLE_SLASH]: getGlobalWorkspace,
  [RouteId.GO]: getGlobalWorkspace,
  [RouteId.INVITE_ACCEPT_BOARD]: getGlobalWorkspace,
  [RouteId.INVITE_ACCEPT_TEAM]: getGlobalWorkspace,

  [RouteId.ENTERPRISE_ADMIN]: getWorkspaceForOrganization,
  [RouteId.ENTERPRISE_ADMIN_TAB]: getWorkspaceForOrganization,

  [RouteId.MEMBER_ACTIVITY]: getWorkspaceForOrganization,
  [RouteId.MEMBER_ALL_BOARDS]: getWorkspaceForOrganization,
  [RouteId.MEMBER_CARDS]: getWorkspaceForOrganization,
  [RouteId.MEMBER_HOME]: getGlobalWorkspace,
  [RouteId.MEMBER_TASKS]: getWorkspaceForOrganization,

  [RouteId.POWER_UP_ADMIN]: getGlobalWorkspace,
  [RouteId.POWER_UP_EDIT]: getGlobalWorkspace,
  [RouteId.POWER_UP_PUBLIC_DIRECTORY]: getGlobalWorkspace,

  [RouteId.PROFILE]: getWorkspaceForOrganization,

  [RouteId.SEARCH]: getGlobalWorkspace,

  [RouteId.SELECT_ORG_TO_UPGRADE]: getGlobalWorkspace,
  [RouteId.SELECT_TEAM_TO_UPGRADE]: getGlobalWorkspace,

  [RouteId.SHORTCUTS]: getGlobalWorkspace,
  [RouteId.SHORTCUTS_OVERLAY]: getGlobalWorkspace,

  [RouteId.TEMPLATES_SUBMIT]: getGlobalWorkspace,
  [RouteId.TEMPLATES]: getGlobalWorkspace,
  [RouteId.TEMPLATES_RECOMMEND]: getGlobalWorkspace,

  [RouteId.TO]: getGlobalWorkspace,
  [RouteId.WELCOME]: getGlobalWorkspace,

  [RouteId.ERROR_PAGE]: noMatchingRouteError,
};

export function getWorkspaceFromPathname(
  pathname: string,
): Promise<WorkspaceState> {
  const routeId = getRouteIdFromPathname(pathname);

  if (!routeId) {
    return noMatchingRouteError(pathname);
  }

  const getter = workspaceGetterMap[routeId];

  const match = routes[routeId].regExp.exec(pathname.slice(1));
  const params = match?.slice(1) ?? [];
  return getter(...params);
}
